import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Brain, Smile, Frown, Zap, AlertTriangle, TrendingUp, TrendingDown, Activity, Award, Target, CheckCircle, XCircle } from 'lucide-react';
import UpgradePrompt from './components/UpgradePrompt';
import { hasActiveSubscription } from './subscriptionUtils';
import { getTrades } from './services/firestoreService';
import './PsychologyTracker.css';

const PsychologyTracker = ({ currentUser, onNavigate }) => {
  const [trades, setTrades] = useState([]);
  const [emotionData, setEmotionData] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [isPaid, setIsPaid] = useState(null); // null = loading

  // Emotion categories with icons and colors
  const emotions = {
    'Calm': { icon: Smile, color: '#10b981', bgColor: 'bg-green-900/30', textColor: 'text-green-400' },
    'Confident': { icon: Award, color: '#3b82f6', bgColor: 'bg-blue-900/30', textColor: 'text-blue-400' },
    'Fearful': { icon: AlertTriangle, color: '#f59e0b', bgColor: 'bg-yellow-900/30', textColor: 'text-yellow-400' },
    'Greedy': { icon: TrendingUp, color: '#ef4444', bgColor: 'bg-red-900/30', textColor: 'text-red-400' },
    'Revenge': { icon: Zap, color: '#dc2626', bgColor: 'bg-red-900/30', textColor: 'text-red-400' },
    'FOMO': { icon: Activity, color: '#f97316', bgColor: 'bg-orange-900/30', textColor: 'text-orange-400' },
    'Anxious': { icon: Frown, color: '#8b5cf6', bgColor: 'bg-purple-900/30', textColor: 'text-purple-400' }
  };

  // Detect psychological patterns — defined before useEffect so the effect closure can call it
  const detectPatterns = (emotionStats, tradesData) => {
    const detectedPatterns = [];

    // Pattern 1: Best performing emotion
    if (emotionStats.length > 0) {
      const best = emotionStats[0];
      if (best.winRate > 60) {
        detectedPatterns.push({
          id: 'best-emotion',
          type: 'positive',
          title: `${best.emotion} = Peak Performance`,
          description: `जब आप ${best.emotion} होते हो, तब ${best.winRate.toFixed(0)}% win rate है।`,
          impact: 'High',
          action: `${best.emotion} state में ज्यादा trade करने की कोशिश करें।`,
          confidence: 88
        });
      }
    }

    // Pattern 2: Worst performing emotion
    const worst = emotionStats[emotionStats.length - 1];
    if (worst && worst.winRate < 40) {
      detectedPatterns.push({
        id: 'worst-emotion',
        type: 'negative',
        title: `${worst.emotion} = High Risk State`,
        description: `${worst.emotion} में only ${worst.winRate.toFixed(0)}% win rate (average loss: ₹${Math.abs(worst.avgPnL).toFixed(0)})`,
        impact: 'Critical',
        action: `${worst.emotion} feel होने पर trading stop करें। 30 min break लें।`,
        confidence: 92
      });
    }

    // Pattern 3: Revenge trading detection
    const revengeEmotion = emotionStats.find(e => e.emotion === 'Revenge');
    if (revengeEmotion && revengeEmotion.totalTrades > 2) {
      detectedPatterns.push({
        id: 'revenge-pattern',
        type: 'critical',
        title: 'Revenge Trading Detected',
        description: `${revengeEmotion.totalTrades} revenge trades with ${revengeEmotion.winRate.toFixed(0)}% win rate`,
        impact: 'Very High',
        action: 'Rule बनाएं: Loss के बाद mandatory 1 hour break। No exceptions!',
        confidence: 95
      });
    }

    // Pattern 4: Fear-based trading
    const fearEmotion = emotionStats.find(e => e.emotion === 'Fearful');
    if (fearEmotion && fearEmotion.avgPnL < 0) {
      detectedPatterns.push({
        id: 'fear-pattern',
        type: 'warning',
        title: 'Fear Causing Losses',
        description: `Fearful trades में average ₹${Math.abs(fearEmotion.avgPnL).toFixed(0)} loss`,
        impact: 'Medium',
        action: 'Small position sizes से confidence build करें। Paper trading practice करें।',
        confidence: 78
      });
    }

    // Pattern 5: FOMO trading
    const fomoEmotion = emotionStats.find(e => e.emotion === 'FOMO');
    if (fomoEmotion && fomoEmotion.winRate < 45) {
      detectedPatterns.push({
        id: 'fomo-pattern',
        type: 'warning',
        title: 'FOMO Leading to Poor Decisions',
        description: `FOMO trades में ${fomoEmotion.winRate.toFixed(0)}% win rate - below average`,
        impact: 'High',
        action: 'Setup-based trading follow करें। "Everyone making money" feeling को ignore करें।',
        confidence: 85
      });
    }

    // Pattern 6: Greedy trading
    const greedEmotion = emotionStats.find(e => e.emotion === 'Greedy');
    if (greedEmotion && greedEmotion.avgPnL < 0) {
      detectedPatterns.push({
        id: 'greed-pattern',
        type: 'warning',
        title: 'Greed Causing Overtrading',
        description: `Greedy state में average loss ₹${Math.abs(greedEmotion.avgPnL).toFixed(0)}`,
        impact: 'High',
        action: 'Fixed target rules बनाएं। Profit booking discipline practice करें।',
        confidence: 82
      });
    }

    setPatterns(detectedPatterns);
  };

  // Generate recommendations — defined before useEffect so the effect closure can call it
  const generateRecommendations = (emotionStats, tradesCount) => {
    const recs = [];

    // Best emotion recommendation
    const best = emotionStats[0];
    if (best) {
      recs.push({
        id: 'optimize-best',
        priority: 'high',
        title: `Maximize ${best.emotion} Trading`,
        description: `आपकी best performance ${best.emotion} state में है। इस state को recognize करना सीखें।`,
        action: `Morning meditation, exercise, या जो भी आपको ${best.emotion} feel कराए - वो routine में add करें।`,
        expectedImpact: `Win rate में 10-15% improvement possible`
      });
    }

    // Avoid worst emotion
    const worst = emotionStats[emotionStats.length - 1];
    if (worst && worst.winRate < 40) {
      recs.push({
        id: 'avoid-worst',
        priority: 'critical',
        title: `${worst.emotion} = No Trading Zone`,
        description: `${worst.emotion} state में trading से बचें - यह आपका biggest weakness है।`,
        action: `${worst.emotion} feel होते ही laptop बंद करें। Walk पर जाएं। Trading app delete करें phone से।`,
        expectedImpact: `Monthly loss में ₹${Math.abs(worst.totalPnL).toFixed(0)} की बचत`
      });
    }

    // Emotional awareness
    recs.push({
      id: 'awareness',
      priority: 'medium',
      title: 'Pre-Trade Emotional Check',
      description: 'हर trade से पहले अपनी emotional state check करें।',
      action: '5-second pause rule: "अभी मैं कैसा feel कर रहा हूं?" - इसका सच्चा answer दें और तभी trade लें।',
      expectedImpact: 'Emotional trading में 60-70% कमी'
    });

    // Journaling
    if (tradesCount < 20) {
      recs.push({
        id: 'journaling',
        priority: 'low',
        title: 'Trading Journal Maintain करें',
        description: 'और ज्यादा trades log करें emotions के साथ - better insights मिलेंगे।',
        action: 'Target: हर trade में emotion mandatory mark करें। कम से कम 30 trades data चाहिए।',
        expectedImpact: 'Better self-awareness और pattern recognition'
      });
    }

    setRecommendations(recs);
  };

  // Analyze emotions and trading performance — defined before useEffect so the effect closure can call it
  const analyzeEmotions = (tradesData) => {
    // Group trades by emotion
    const emotionStats = {};

    tradesData.forEach(trade => {
      const emotion = trade.emotion || 'Unknown';

      if (!emotionStats[emotion]) {
        emotionStats[emotion] = {
          emotion,
          totalTrades: 0,
          wins: 0,
          losses: 0,
          totalPnL: 0,
          avgPnL: 0,
          winRate: 0
        };
      }

      emotionStats[emotion].totalTrades++;
      emotionStats[emotion].totalPnL += trade.pnl;

      if (trade.pnl > 0) {
        emotionStats[emotion].wins++;
      } else {
        emotionStats[emotion].losses++;
      }
    });

    // Calculate averages and win rates
    const emotionArray = Object.values(emotionStats).map(stat => ({
      ...stat,
      avgPnL: stat.totalPnL / stat.totalTrades,
      winRate: (stat.wins / stat.totalTrades) * 100
    }));

    // Sort by win rate
    emotionArray.sort((a, b) => b.winRate - a.winRate);

    setEmotionData(emotionArray);

    // Detect patterns
    detectPatterns(emotionArray, tradesData);

    // Generate recommendations (pass tradesData.length to avoid stale state)
    generateRecommendations(emotionArray, tradesData.length);
  };

  // Load user's trades and subscription status from Firestore
  useEffect(() => {
    // Clear stale data immediately on any user change
    setTrades([]);
    setEmotionData([]);
    setPatterns([]);
    setRecommendations([]);
    setIsPaid(null);

    if (currentUser) {
      console.log('[PsychologyTracker] Loading for user:', currentUser.id);
      const load = async () => {
        const paid = await hasActiveSubscription(currentUser.id);
        setIsPaid(paid);
        if (paid) {
          const userTrades = await getTrades(currentUser.id);
          console.log('[PsychologyTracker] Fetched', userTrades.length, 'trades for user:', currentUser.id);
          setTrades(userTrades);
          if (userTrades.length > 0) analyzeEmotions(userTrades);
        }
      };
      load();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gate: loading state
  if (isPaid === null) return null;

  // Gate: require active subscription
  if (!isPaid) {
    return (
      <UpgradePrompt
        requiredPlan="pro"
        feature="Psychology Tracker Pro plan mein available hai. Apne emotions aur patterns track karo."
      />
    );
  }

  // Prepare chart data
  const prepareEmotionChartData = () => {
    return emotionData.map(e => ({
      emotion: e.emotion,
      winRate: parseFloat(e.winRate.toFixed(1)),
      avgPnL: parseFloat(e.avgPnL.toFixed(0)),
      trades: e.totalTrades
    }));
  };

  // Prepare pie chart data
  const preparePieChartData = () => {
    return emotionData.map(e => ({
      name: e.emotion,
      value: e.totalTrades
    }));
  };

  const chartData = emotionData.length > 0 ? prepareEmotionChartData() : [];
  const pieData = emotionData.length > 0 ? preparePieChartData() : [];

  // Colors for charts
  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316', '#ec4899'];

  if (trades.length === 0) {
    return (
      <div className="pt-card pt-empty">
        <Brain size={64} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
        <h3 className="pt-page-title" style={{ fontSize: 22, textAlign: 'center' }}>No Trading Data</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>पहले trades add करें My Trades section में</p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Emotions के साथ trades log करें better insights के लिए!</p>
      </div>
    );
  }

  // Check if trades have emotion data
  const tradesWithEmotions = trades.filter(t => t.emotion).length;
  if (tradesWithEmotions === 0) {
    return (
      <div className="pt-card pt-empty">
        <Brain size={64} className="mx-auto mb-4" style={{ color: 'var(--accent)' }} />
        <h3 className="pt-page-title" style={{ fontSize: 22, textAlign: 'center' }}>Emotion Tracking Not Available</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>आपके trades में emotion data नहीं है</p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          नए trades add करते समय emotion select करें psychology analysis के लिए
        </p>
        <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', padding: 16, borderRadius: 8, textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>💡 Tip: Emotions को track करने से:</p>
          <ul style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8, paddingLeft: 0, listStyle: 'none' }}>
            <li>• Pattern recognition improve होता है</li>
            <li>• Better self-awareness develop होती है</li>
            <li>• Emotional trading reduce होती है</li>
            <li>• Win rate में improvement आता है</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pt-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
              <Brain size={22} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <h2 className="pt-page-title" style={{ fontSize: 22 }}>Psychology Tracker</h2>
              <p className="pt-page-subtitle" style={{ margin: 0 }}>Emotion-Based Trading Analysis</p>
            </div>
          </div>
          <div className="pt-stat" style={{ marginBottom: 0 }}>
            <p className="pt-stat-label">Analyzing</p>
            <p className="pt-stat-value" style={{ fontSize: 22 }}>{tradesWithEmotions} Emotional Trades</p>
          </div>
        </div>
      </div>

      {/* Emotion Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {emotionData.slice(0, 3).map((emotion, idx) => {
          const emotionConfig = emotions[emotion.emotion] || emotions['Calm'];
          const Icon = emotionConfig.icon;

          return (
            <div
              key={emotion.emotion}
              className="pt-card"
              style={{ borderLeft: `4px solid ${emotionConfig.color}`, marginBottom: 0 }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 rounded-full" style={{ background: 'var(--bg-primary)' }}>
                  <Icon size={24} style={{ color: emotionConfig.color }} />
                </div>
                <span className="pt-badge" style={{ background: 'var(--bg-primary)', color: emotionConfig.color, borderColor: emotionConfig.color }}>
                  {idx === 0 ? 'BEST' : idx === 1 ? 'GOOD' : 'OK'}
                </span>
              </div>
              <h3 className="pt-entry-title">{emotion.emotion}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}>Win Rate:</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{emotion.winRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}>Avg P&L:</span>
                  <span style={{ fontWeight: 700, color: emotion.avgPnL >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                    ₹{emotion.avgPnL.toFixed(0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}>Total Trades:</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{emotion.totalTrades}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Win Rate by Emotion Chart */}
      <div className="pt-chart-card">
        <h3 className="pt-chart-title" style={{ fontSize: 16, fontWeight: 700 }}>Win Rate by Emotional State</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="emotion" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
            <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
            <Legend wrapperStyle={{ color: 'var(--text-secondary)' }} />
            <Bar dataKey="winRate" fill="var(--accent)" name="Win Rate (%)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Emotion Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="pt-chart-card" style={{ marginBottom: 0 }}>
          <h3 className="pt-chart-title" style={{ fontSize: 16, fontWeight: 700 }}>Emotion Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="pt-chart-card" style={{ marginBottom: 0 }}>
          <h3 className="pt-chart-title" style={{ fontSize: 16, fontWeight: 700 }}>Average P&L by Emotion</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
              <YAxis dataKey="emotion" type="category" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Legend wrapperStyle={{ color: 'var(--text-secondary)' }} />
              <Bar dataKey="avgPnL" fill="#3b82f6" name="Avg P&L (₹)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Psychological Patterns */}
      <div className="pt-card">
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity style={{ color: 'var(--accent)' }} />
          <span>Psychological Patterns Detected</span>
        </h3>

        {patterns.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto mb-3" style={{ color: 'var(--profit)' }} />
            <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-secondary)' }}>No Critical Patterns Detected</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Keep logging emotions for better insights!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {patterns.map((pattern) => (
              <div
                key={pattern.id}
                style={{
                  padding: 16,
                  borderRadius: 8,
                  borderLeft: `4px solid ${
                    pattern.type === 'critical' ? 'var(--loss)' :
                    pattern.type === 'negative' ? 'var(--warning)' :
                    pattern.type === 'warning' ? 'var(--warning)' :
                    'var(--profit)'
                  }`,
                  background: pattern.type === 'critical' ? 'var(--danger-bg)' :
                    pattern.type === 'negative' ? 'var(--warning-bg)' :
                    pattern.type === 'warning' ? 'var(--warning-bg)' :
                    'var(--success-bg)'
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{pattern.title}</h4>
                  <div className="flex items-center space-x-2">
                    <span style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                      {pattern.impact} Impact
                    </span>
                    <span style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                      {pattern.confidence}% confidence
                    </span>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 8, fontSize: 14 }}>{pattern.description}</p>
                <div className="pt-ai-feedback">
                  <p className="pt-ai-label">💡 Action:</p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{pattern.action}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="pt-card">
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Target style={{ color: 'var(--accent)' }} />
          <span>Psychological Recommendations</span>
        </h3>
        <div className="space-y-3">
          {recommendations.map((rec) => (
            <div
              key={rec.id}
              style={{
                padding: 16,
                borderRadius: 8,
                border: `1px solid ${
                  rec.priority === 'critical' ? 'var(--loss)' :
                  rec.priority === 'high' ? 'var(--warning)' :
                  rec.priority === 'medium' ? 'var(--accent-glow)' :
                  'var(--border)'
                }`,
                background: rec.priority === 'critical' ? 'var(--danger-bg)' :
                  rec.priority === 'high' ? 'var(--warning-bg)' :
                  rec.priority === 'medium' ? 'var(--accent-subtle)' :
                  'var(--bg-primary)'
              }}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{rec.title}</h4>
                <span style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                  {rec.priority.toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>{rec.description}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{rec.action}</p>
              <p style={{ color: 'var(--profit)', fontSize: 12, fontWeight: 600 }}>
                📊 {rec.expectedImpact}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Emotional State Guide */}
      <div className="pt-card">
        <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16 }}>Ideal Trading Mindset</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div style={{ padding: 16, borderRadius: 8, background: 'var(--success-bg)', border: '1px solid var(--profit)' }}>
            <h4 style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--profit)' }}>
              <CheckCircle size={20} />
              <span>✅ Trade When:</span>
            </h4>
            <ul style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--text-secondary)', listStyle: 'none', padding: 0 }}>
              <li>• Calm और composed feel हो रहा है</li>
              <li>• Plan clear है, setup confirm है</li>
              <li>• Risk management follow कर रहे हो</li>
              <li>• Patient और disciplined mood में हो</li>
            </ul>
          </div>
          <div style={{ padding: 16, borderRadius: 8, background: 'var(--danger-bg)', border: '1px solid var(--loss)' }}>
            <h4 style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--loss)' }}>
              <XCircle size={20} />
              <span>❌ Don't Trade When:</span>
            </h4>
            <ul style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--text-secondary)', listStyle: 'none', padding: 0 }}>
              <li>• Revenge या FOMO feel हो रहा है</li>
              <li>• Frustrated या angry हो</li>
              <li>• Big loss के तुरंत बाद</li>
              <li>• Sleep या food की कमी हो</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PsychologyTracker;
